import { msalInstance } from '../msalInstance';

// Microsoft Graph helpers. The `User.Read` delegated permission (already in
// loginRequest per authConfig.ts) covers the /me and /me/photo endpoints, so
// we can silently acquire a Graph token and hit them without additional
// consent. Graph tokens are separate from the Manager API token — MSAL
// returns different tokens depending on the requested scope.

const GRAPH_SCOPES = ['User.Read'];
const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

async function acquireGraphToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: GRAPH_SCOPES,
      account,
    });
    return result.accessToken;
  } catch {
    // Silent acquisition can fail for benign reasons (stale cache, revoked
    // consent). We don't force interaction here — the avatar falls back to
    // initials and the app keeps working.
    return null;
  }
}

// Fetches the signed-in user's profile photo from Microsoft Graph and returns
// it as a data URL. Returns null if the user has no photo (404), the token
// acquisition fails, or the network call errors. Data URL over Blob URL so
// the caller doesn't have to manage `URL.revokeObjectURL` cleanup — a
// per-user photo is small (typically <20 KB) so the base64 overhead is
// negligible.
export async function getUserPhotoDataUrl(): Promise<string | null> {
  const token = await acquireGraphToken();
  if (!token) return null;

  const response = await fetch(GRAPH_PHOTO_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 404) {
    // User has no profile photo — common, not an error.
    return null;
  }
  if (!response.ok) return null;

  const blob = await response.blob();
  return await new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}
